import { LoadingButton } from "@mui/lab";
import { useForm } from "react-hook-form";
import { Box, SxProps } from "@mui/material";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";

import { CreatePostFormData } from "../../../../api/types/feed.types";
import { createPostSchema } from "../../../../schemas/createPost.schema";
import { useCreateBasicPostMutation } from "../../../../api/mutations/useCreateBasicPostMutation";

import { TagsField } from "../../../../components/basicPostFields/TagsField";
import { MediaField } from "../../../../components/basicPostFields/MediaField";
import { PrivacyField } from "../../../../components/basicPostFields/PrivacyField";
import { DescriptionField } from "../../../../components/basicPostFields/DescriptionField";
import { AchievementsField } from "../../../../components/basicPostFields/AchievementsField";

export const commonStyles = {
  formControl: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  } as SxProps,
  formLabel: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  } as SxProps,
  textField: {
    "& .MuiInputBase-root.MuiOutlinedInput-root": {
      borderRadius: "8px",
    },
  } as SxProps,
  radioGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  } as SxProps,
  radioLabel: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  } as SxProps,
  radioDescription: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "18px",
  } as SxProps,
  autocompleteStyles: {
    "& .MuiInputBase-root.MuiOutlinedInput-root": {
      borderRadius: "8px",
    },
  } as SxProps,
  autocompleteChipStyles: {
    borderRadius: "15px",
    border: "1px solid #CFCFCF",
    color: "#414141",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    margin: "2px",
    width: "100px",
    "& .MuiChip-label": {
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      padding: "0 8px",
      width: "100%",
    },
  } as SxProps,
};

const CreatePostForm = () => {
  const { t } = useTranslation("feed", { keyPrefix: "postForm" });
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreatePostFormData>({
    resolver: zodResolver(createPostSchema),
    defaultValues: {
      description: "",
      media: [],
      tags: {
        old: [],
        new: [],
      },
      privacy: "PUBLIC",
      achievementId: null,
    },
    mode: "onChange",
  });

  const { createPost, isCreating } = useCreateBasicPostMutation();

  const handleFormSubmit = handleSubmit((data: CreatePostFormData) => {
    const dataToSubmit = {
      ...data,
      selectedUsers: data.privacy === "PRIVATE" ? data.selectedUsers : [],
      selectedGroups: data.privacy === "PRIVATE" ? data.selectedGroups : [],
    };

    createPost(dataToSubmit);
  });

  return (
    <Box component="form" onSubmit={handleFormSubmit} sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Box sx={{ display: "flex", gap: "48px", flexDirection: { xs: "column", sm: "row" } }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "24px" }}>
          <DescriptionField control={control} name="description" error={errors.description?.message} />
          <AchievementsField control={control} name="achievementId" error={errors.achievementId?.message} />
          <TagsField control={control} name="tags" error={errors.tags?.message} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <MediaField control={control} name="media" error={errors.media?.message} />
        </Box>
      </Box>

      <PrivacyField control={control} name="privacy" error={errors.privacy?.message} />

      <LoadingButton
        variant="primary-md"
        type="submit"
        disabled={isSubmitting}
        sx={{ width: "fit-content", marginLeft: "auto" }}
        loading={isCreating}
      >
        {t("publish")}
      </LoadingButton>
    </Box>
  );
};

export default CreatePostForm;
